/**
 * Fuel price list (ZeeCore FuelPriceDeclared entries with an effective-from
 * date): load the history and add an entry. Entries are never edited — a new
 * price or a correction is a new entry, and the newest for a date wins.
 */

import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from './apiClient';
import { DECLARED_INGEST_TIMEOUT_MS } from './fuelTransactionUpload';
import { isValidCalendarYmd } from './calendarYmd';
import { KEYFUELS_DIESEL_PRICE_MAX_PPL } from './fuelPriceLimits';

export const FUEL_PRICE_LIST_FUEL_TYPES = ['diesel', 'adblue', 'petrol', 'hvo', 'other'] as const;
export type FuelPriceListFuelType = (typeof FUEL_PRICE_LIST_FUEL_TYPES)[number];

export type FuelPriceListEntry = {
  event_id: string;
  fuel_type: FuelPriceListFuelType;
  site_name: string | null;
  effective_from: string;
  pence_per_litre: number;
  provenance_class: 'Asserted' | 'Estimated';
  basis?: string;
  entered_by?: string;
  entry_method: string;
  received_at: string;
  /** A newer entry for the same fuel, site and date replaced this one. */
  corrected: boolean;
};

export type FuelPriceListEntryInput = {
  fuel_type: FuelPriceListFuelType;
  site_name?: string;
  effective_from: string;
  pence_per_litre: number;
  provenance_class: 'Asserted' | 'Estimated';
  basis?: string;
};

export async function fetchFuelPriceList(): Promise<FuelPriceListEntry[]> {
  const response = await authenticatedFetch(
    API_ENDPOINTS.fleetIntelligence.fuelPriceList(),
    { method: 'GET' },
    DECLARED_INGEST_TIMEOUT_MS,
  );
  const json = (await response.json().catch(() => ({}))) as { entries?: FuelPriceListEntry[]; error?: string };
  if (!response.ok) throw new Error(json.error ?? `Loading the fuel price list failed (HTTP ${response.status}).`);
  return json.entries ?? [];
}

/** Same checks as the server, so the form can say what is wrong before saving. */
export function fuelPriceEntryError(input: FuelPriceListEntryInput): string | null {
  if (!isValidCalendarYmd(input.effective_from)) return 'Pick the date the price applies from.';
  if (!Number.isFinite(input.pence_per_litre) || input.pence_per_litre <= 0) return 'Enter a price in pence per litre above 0.';
  if (input.pence_per_litre > KEYFUELS_DIESEL_PRICE_MAX_PPL) return `Price must be at most ${KEYFUELS_DIESEL_PRICE_MAX_PPL}p per litre.`;
  if (input.provenance_class === 'Estimated' && !input.basis?.trim()) return 'Say how the estimate was made.';
  return null;
}

export async function addFuelPriceEntry(input: FuelPriceListEntryInput): Promise<void> {
  const problem = fuelPriceEntryError(input);
  if (problem) throw new Error(problem);
  const response = await authenticatedFetch(
    API_ENDPOINTS.declaredIngest.fuelPrice(),
    {
      method: 'POST',
      body: JSON.stringify({
        ...input,
        site_name: input.site_name?.trim() || undefined,
        basis: input.basis?.trim() || undefined,
        entry_method: 'manual',
      }),
    },
    DECLARED_INGEST_TIMEOUT_MS,
  );
  if (!response.ok) {
    const json = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(json.error ?? `Saving the price failed (HTTP ${response.status}).`);
  }
}
